import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRightCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  usePromoteQuote,
  type QuickQuote,
} from "@/hooks/useQuickQuotes";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: QuickQuote | null;
  onPromoted?: (dealId: string) => void;
}

const fmtBRL = (n: number | null | undefined) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n ?? 0);

export function QuickQuotePromoteDialog({
  open,
  onOpenChange,
  quote,
  onPromoted,
}: Props) {
  const navigate = useNavigate();
  const promote = usePromoteQuote();
  const [submitting, setSubmitting] = useState(false);

  if (!quote) return null;

  const handlePromote = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const data = await promote.mutateAsync(quote.id);
      const dealId = data?.deal_id as string;
      const wasNewLead = !!data?.was_new_lead;
      const already = !!data?.already_promoted;
      toast.success(
        already
          ? "Cotação já estava promovida — abrindo oportunidade"
          : wasNewLead
          ? `Oportunidade criada — novo lead cadastrado`
          : `Oportunidade criada com lead existente`,
      );
      onPromoted?.(dealId);
      onOpenChange(false);
      if (dealId) navigate(`/app/crm/oportunidade/${dealId}`);
    } catch (err) {
      toast.error("Erro ao promover cotação", {
        description: (err as any)?.message || String(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Promover cotação para oportunidade?</DialogTitle>
          <DialogDescription>
            Vamos verificar se o cliente já existe no CRM e criar uma nova
            oportunidade na primeira etapa do funil. Se o cliente for novo, ele
            também será cadastrado como lead automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-muted/40 p-4 text-sm space-y-1">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Cotação</span>
            <span className="font-medium">{quote.code}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Cliente</span>
            <span className="font-medium truncate">{quote.client_name || "—"}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Telefone</span>
            <span className="font-medium">{quote.client_phone || "—"}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">Valor estimado</span>
            <span className="font-medium">
              {fmtBRL(quote.total_value ?? quote.investment_min)}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button onClick={handlePromote} disabled={submitting}>
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowRightCircle className="mr-2 h-4 w-4" />
            )}
            Promover
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
